import { FC, useState } from "react";
import Cards, { CallbackArgument, Focused } from "react-credit-cards";
import "react-credit-cards/es/styles-compiled.css";
import { Controller, useForm } from "react-hook-form";
import NumberFormat from "react-number-format";
import styled from "styled-components";
import { IProfessionalSchedulesAvailable } from "../../api/getProfessionals";
import Button from "../buttons/Button";
import TextField from "../fields/TextField";
import {
	BuyDetailsInterface,
	CheckoutStepperBody,
	CheckoutStepperFooter,
	CheckoutStepperHeader,
	CheckoutStepperWrapper,
	FooterButtonWrapper,
	FormFieldWrapper,
	FormRow,
	InputLabel,
	ItemBuy,
	StepFormProps,
	YourBuyRow,
	YourBuyWrapper,
	TermsLabel,
	TermsWrapper,
} from "./CheckoutCommon";

const CheckoutStepperSubHeader = styled.div`
	background: ${props => props.theme.colors.lightGrayVitat || "#f2f2f8"};
	border-bottom: 5px solid ${props => props.theme.colors.brownVitat || props.theme.colors.primary};
	padding-top: 8px;
	padding-left: 12px;
	padding-right: 12px;
	padding-bottom: 5px;
	font-family: Livvic;
	font-style: normal;
	font-weight: 600;
	font-size: 13px;
	line-height: 165.5%;
	color: ${props => props.theme.colors.blackVitat || "#002b49"};
	display: flex;
	flex-direction: row;
	flex-wrap: nowrap;
	justify-content: space-between;
	align-items: center;
	align-content: center;
	p {
		margin: 0px;
	}
`;

const FieldErrorShow = styled.p`
	font-family: Livvic;
	font-style: normal;
	font-weight: 500;
	font-size: 12px;
	line-height: 165.5%;
	color: #943737;
	margin-bottom: 8px;
	text-align: left;
	margin-left: 5px;
`;

// const WarningMessage = styled.p`
// 	font-family: Livvic;
// 	font-style: normal;
// 	font-weight: 800;
// 	font-size: 12px;
// 	line-height: 165.5%;
// 	color: #d47628;
// 	margin-bottom: 8px;
// 	text-align: center;
// 	width: 100%;
// `;

interface PurchaseDetailsStepProps extends StepFormProps {
	scheduleInfo?: IProfessionalSchedulesAvailable;
	purchaseData?: BuyDetailsInterface;
}

const PurchaseDetailsStep: FC<PurchaseDetailsStepProps> = ({ scheduleInfo, carregaProximoPasso, purchaseData }) => {
	const [focusCartao, setFocusCartao] = useState<Focused>("name"); //"name" | "number" | "expiry" | "cvc";

	const {
		register,
		watch,
		control,
		handleSubmit,
		setError,
		setValue,
		formState: { errors },
	} = useForm<BuyDetailsInterface>({
		defaultValues: purchaseData
			? purchaseData
			: process.env.NODE_ENV === "development"
			? {
					nome: "Julio Alvarenga",
					numero: "4539003370725497",
					validade: "03/2027",
					cvv: "123",
					bandeira: "visa",
			  }
			: {
					nome: "",
					numero: "",
					validade: "",
					cvv: "",
					bandeira: "",
			  },
	});

	const onSubmit = handleSubmit((data: BuyDetailsInterface) => {
		const numero = data.numero.replace(/[^0-9]/g, "");
		const validade = data.validade.replace(/[^0-9/]/g, "");
		const cvv = data.cvv.replace(/[^0-9]/g, "");

		if (numero.length < 16) {
			setError("numero", { type: "minLength" });
			return;
		}
		if (validade.length < 5) {
			setError("validade", { type: "minLength" });
			return;
		}
		// TODO: ajustar envio validade, deve ir separada: {mes, ano}
		if (parseInt(validade.split("/")[0]) > 12 || parseInt(validade.split("/")[0]) == 0) {
			setError("validade", { type: "invelid", message: "Mês inválido" });
			return;
		}
		if (cvv.length < 3) {
			setError("cvv", { type: "minLength" });
			return;
		}

		carregaProximoPasso({ ...data, numero, validade, cvv });
	});

	const handleCardChange = (type: CallbackArgument) => {
		setValue("bandeira", type.issuer);
	};

	return (
		<CheckoutStepperWrapper>
			<CheckoutStepperHeader>
				<p>Detalhes da compra</p>
			</CheckoutStepperHeader>
			<YourBuyWrapper>
				<YourBuyRow>
					<InputLabel>Sua compra:</InputLabel>
				</YourBuyRow>
				{scheduleInfo && (
					<ItemBuy descricao={`${scheduleInfo.procedimento.descricao} - ${scheduleInfo.convenio} `} valor={scheduleInfo.valor} quantidade={1} />
				)}
			</YourBuyWrapper>
			<CheckoutStepperSubHeader>
				<p>Adicione os dados do cartão:</p>
			</CheckoutStepperSubHeader>
			<CheckoutStepperBody>
				{/* {rescheduleId && (
					<FormRow gap={14} margin={5}>
						<WarningMessage>Após finalizar este reagendamento o pagamento anterior será estornado</WarningMessage>
					</FormRow>
				)} */}
				<div style={{ marginTop: "10px" }}></div>
				<Cards
					cvc={watch("cvv")}
					expiry={watch("validade")}
					focused={focusCartao}
					name={watch("nome")}
					number={watch("numero")}
					locale={{
						valid: "Validade",
					}}
					placeholders={{ name: "SEU NOME" }}
					callback={handleCardChange}
				/>
				<FormRow>
					<FormFieldWrapper>
						<InputLabel>Nome impresso no cartão</InputLabel>
						<TextField
							{...register("nome", { required: true })}
							placeholder="João da Silva"
							onFocus={() => setFocusCartao("name")}
							errorMode={errors.nome != undefined}
							type={"text"}
						/>
						{errors.nome && <FieldErrorShow>{errors.nome.message || "Campo obrigatório"}</FieldErrorShow>}
					</FormFieldWrapper>
				</FormRow>
				<FormRow>
					<FormFieldWrapper>
						<InputLabel>Número do cartão</InputLabel>
						<Controller
							control={control}
							name={"numero"}
							render={({ field: { value, onChange } }) => (
								// eslint-disable-next-line @typescript-eslint/ban-ts-comment
								// @ts-ignore
								<NumberFormat
									errorMode={errors.numero != undefined}
									format="#### #### #### ####"
									mask="_"
									placeholder="____ ____ ____ ____"
									customInput={TextField}
									inputMode="numeric"
									textAlign="center"
									letterSpacing={0.235}
									value={value}
									onChange={onChange}
									onFocus={() => setFocusCartao("number")}
								/>
							)}
							rules={{ required: true }}
						/>
						{errors.numero && <FieldErrorShow>{errors.numero.message || "Campo obrigatório"}</FieldErrorShow>}
					</FormFieldWrapper>
				</FormRow>

				<FormRow gap={14} margin={5}>
					<FormFieldWrapper>
						<InputLabel>Validade</InputLabel>
						<Controller
							control={control}
							name={"validade"}
							render={({ field: { value, onChange } }) => (
								// eslint-disable-next-line @typescript-eslint/ban-ts-comment
								// @ts-ignore
								<NumberFormat
									format="##/####"
									mask="_"
									placeholder="__/____"
									customInput={TextField}
									inputMode="numeric"
									textAlign="center"
									letterSpacing={0.235}
									value={value}
									onChange={onChange}
									onFocus={() => setFocusCartao("expiry")}
									errorMode={errors.validade != undefined}
								/>
							)}
							rules={{ required: true }}
						/>
						{errors.validade && <FieldErrorShow>{errors.validade.message || "Campo obrigatório"}</FieldErrorShow>}
					</FormFieldWrapper>

					<FormFieldWrapper>
						<InputLabel>CVV</InputLabel>
						<Controller
							control={control}
							name={"cvv"}
							render={({ field: { value, onChange } }) => (
								// eslint-disable-next-line @typescript-eslint/ban-ts-comment
								// @ts-ignore
								<NumberFormat
									format="###"
									mask="_"
									placeholder="___"
									customInput={TextField}
									inputMode="numeric"
									textAlign="center"
									letterSpacing={0.235}
									value={value}
									onChange={onChange}
									onFocus={() => setFocusCartao("cvc")}
									errorMode={errors.cvv != undefined}
								/>
							)}
							rules={{ required: true }}
						/>
						{errors.cvv && <FieldErrorShow>{errors.cvv.message || "Campo obrigatório"}</FieldErrorShow>}
					</FormFieldWrapper>
				</FormRow>

				<FormRow>
					<TermsWrapper>
						<TermsLabel>Ao prosseguir, eu concordo com os <b>Termos de Adesão.</b></TermsLabel>
					</TermsWrapper>
				</FormRow>
			</CheckoutStepperBody>
			<CheckoutStepperFooter>
				<FooterButtonWrapper>
					<Button type="submit" onClick={onSubmit}>
						Próximo
					</Button>
				</FooterButtonWrapper>
			</CheckoutStepperFooter>
		</CheckoutStepperWrapper>
	);
};

export default PurchaseDetailsStep;
